import * as Sentry from '@sentry/browser';

import { config } from 'src/config/config';
import i18n from 'src/config/i18n';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { AuthenticationService } from 'src/modules/auth/services/AuthenticationService';

export interface CustomError extends Error {
    code?: number;
    body?: Record<string, string>;
}

export class Http {
    private authenticationService: AuthenticationService;

    public constructor() {
        this.authenticationService = new AuthenticationService(
            this,
            toastNotificationsService,
            navigationService,
        );
    }

    public GET_UNAUTHENTICATED<R>(path: string, useUrlEncoded: boolean = false): Promise<R> {
        return this.makeUnAuthenticatedRequest<R>(
            'GET',
            config.apiAddress + path,
            null,
            useUrlEncoded,
        );
    }

    public GET<R>(path: string): Promise<R> {
        return this.makeAuthenticatedRequest<R>('GET', config.apiAddress + path);
    }

    public POST<P, R = P>(path: string, body: P): Promise<R> {
        return this.makeAuthenticatedRequest<P, R>('POST', config.apiAddress + path, body);
    }

    public POST_UNAUTHENTICATED<P, R = P>(path: string, body: P): Promise<R> {
        return this.makeUnAuthenticatedRequest<P, R>('POST', config.apiAddress + path, body);
    }

    public PUT<P, R = P>(path: string, body: P): Promise<R> {
        return this.makeAuthenticatedRequest<P, R>('PUT', config.apiAddress + path, body);
    }

    public PATCH<P, R = P>(path: string, body: P): Promise<R> {
        return this.makeAuthenticatedRequest<P, R>('PATCH', config.apiAddress + path, body);
    }

    public formDataPATCH<P, R = P>(path: string, body: P): Promise<R> {
        return this.makeAuthenticatedRequest<P, R>('PATCH', config.apiAddress + path, body, true);
    }

    public DELETE<R>(path: string): Promise<R> {
        return this.makeAuthenticatedRequest<R>('DELETE', config.apiAddress + path);
    }

    private prepareFormData<T>(data: T): FormData {
        const formData = new FormData();

        Object.entries(data).forEach(([key, value]) => formData.append(key, value || ''));

        return formData;
    }

    public async makeUnAuthenticatedRequest<P, R = P>(
        method: string,
        path: string,
        data: P | FormData | null = null,
        useUrlEncoded: boolean = true,
    ): Promise<R> {
        const options = this.prepareOptions(method, data, useUrlEncoded);

        const res = await fetch(path, options);

        return this.handleResponse(res);
    }

    private async makeAuthenticatedRequest<P, R = P>(
        method: string,
        path: string,
        data: P | null = null,
        useFormData: boolean = false,
    ): Promise<R> {
        const options = this.prepareOptions(method, data, false, useFormData);

        const res = await this.authenticationService.requestWithAuth(path, options);

        return this.handleResponse(res);
    }

    private async handleResponse<T>(res: Response): Promise<T> {
        if (res.ok) {
            return res.json();
        }

        const err: CustomError = new Error(res.statusText);
        err.code = res.status;
        const contentType = res.headers.get('content-type');

        err.body =
            contentType && contentType.includes('application/json')
                ? await res.json()
                : await res.text();

        // To avoid getting errors that user couldn't login because of invalid credentials
        // we filter also validation errors
        const filteredErrors = [400, 401];
        if (!err.code || !filteredErrors.includes(err.code)) {
            Sentry.setExtra('Error code', err.code);
            Sentry.setExtra('Error response', err.body);
            Sentry.captureException(err);
        }

        return Promise.reject(err);
    }

    private prepareOptions(
        method: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any,
        useUrlEncoded?: boolean,
        useFormData?: boolean,
    ): RequestInit {
        let body: string | null | FormData;

        if (useUrlEncoded) {
            body = Object.keys(data)
                .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
                .join('&');
        } else if (useFormData) {
            body = this.prepareFormData(data);
        } else if (data) {
            body = JSON.stringify(data);
        } else {
            body = null;
        }

        const headers = new Headers();

        if (!useFormData) {
            headers.append(
                'Content-Type',
                useUrlEncoded
                    ? 'application/x-www-form-urlencoded;charset=UTF-8'
                    : 'application/json',
            );
        }

        headers.append('Accept-Language', i18n.language);

        return {
            body,
            headers,
            method,
        };
    }
}
